import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api } from "../lib/api";

/**
 * Multi-select ambient-mood chips.
 * Renders 4 groups (Ambiance / Light / Yard / Weather). Each chip toggles on click.
 *
 * Props:
 *   selected: string[]  — array of mood ids currently picked
 *   onChange: (ids: string[]) => void
 *   compact?: boolean   — smaller spacing for use inside reel-shot rows
 */
export default function MoodChips({ selected = [], onChange, compact = false }) {
  const [moods, setMoods] = useState([]);
  useEffect(() => {
    api
      .get("/moods")
      .then(({ data }) => setMoods(data))
      .catch(() => {});
  }, []);

  const toggle = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter((x) => x !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  if (moods.length === 0) return null;
  const grouped = moods.reduce((acc, m) => {
    (acc[m.category] = acc[m.category] || []).push(m);
    return acc;
  }, {});

  return (
    <div className={compact ? "space-y-2" : "space-y-3"}>
      {Object.entries(grouped).map(([category, items]) => (
        <div key={category}>
          <div
            className={`text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1.5 ${
              compact ? "mb-1" : ""
            }`}
          >
            {category}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {items.map((m) => {
              const active = selected.includes(m.id);
              const Icon = Icons[m.icon] || Icons.Sparkles;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggle(m.id)}
                  title={m.descriptor}
                  data-testid={`mood-${m.id}`}
                  className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-all ${
                    active
                      ? "bg-amber-500/15 border-amber-500/40 text-amber-200"
                      : "bg-white/5 border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
                  }`}
                >
                  <Icon size={11} />
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {selected.length > 0 && (
        <div className="flex items-center justify-between text-[10px] text-zinc-600 pt-1">
          <span>{selected.length} mood{selected.length === 1 ? "" : "s"} applied</span>
          <button
            onClick={() => onChange([])}
            className="text-zinc-500 hover:text-white underline underline-offset-2"
          >
            clear
          </button>
        </div>
      )}
    </div>
  );
}
